import { Component, DestroyRef, effect, HostListener, Inject, OnInit, signal } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { RootState } from '@root/app.state';

import * as PlatformActions from '@platform/platform.actions';
import { go } from '@platform/router.actions';
import { requireSelection } from '@platform/shared/utilities/require-selection-validator';
import { selectNavigatedCustomerSpace } from '@core/core.selectors';
import { CustomerSpaceUser, getUserDisplayName, Role, RoleName, User } from '@core/core.state';
import { CustomerSpaceApp, getPermissionForApp } from '@core/customer-space-admin/app-permission-helper';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { OptionsStoreService, UiSingleOptions } from '@platform/components/exper-table/sort-options-store.service';

@Component({
  selector: 'app-update-employee-dialog',
  templateUrl: './update-employee-dialog.component.html',
  styleUrls: ['./update-employee-dialog.component.scss'],
})
export class UpdateEmployeeDialogComponent implements OnInit {
  public updateEmployeeForm!: UntypedFormGroup;
  public appPermissionsForm!: UntypedFormGroup;

  public filteredUsers$!: Observable<User[]>;
  public roles: Role[] = [];
  public isEditMode: boolean;
  public userId: string | null = null;
  public isInternalUser!: boolean;
  public hasAbacusInstanceAndMandator!: boolean;

  randomName1 = Math.random().toString();
  randomName2 = Math.random().toString();

  filterUsersByCustomerSpace = signal(
    this.optionsStore.getOptionOrDefault(UiSingleOptions.ClientAdminUpdateEmployeeDialogFilterUsersByCustomerSpace, true),
  );

  private customerSpaceName: string;
  private customerSpaceRoles: Role[];
  private activeUsers: User[];
  private customerSpaceId: string;
  private abacusMandatorId!: string;

  constructor(
    private dialogRef: MatDialogRef<UpdateEmployeeDialogComponent>,
    private store$: Store<RootState>,
    private formBuilder: UntypedFormBuilder,
    private translate: TranslateService,
    private optionsStore: OptionsStoreService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private destroyRef: DestroyRef,
  ) {
    this.activeUsers = data.activeUsers;
    this.customerSpaceRoles = data.customerSpaceRoles;
    this.isEditMode = !!data.selectedEmployee;
    this.customerSpaceId = data.customerSpaceId;

    this.setupEmployeeForm(data.selectedEmployee);
    this.setupAppPermissionForm();

    effect(() => {
      this.optionsStore.setOption(
        UiSingleOptions.ClientAdminUpdateEmployeeDialogFilterUsersByCustomerSpace,
        this.filterUsersByCustomerSpace(),
      );
    });
  }

  @HostListener('document:keydown.enter', ['$event'])
  enter($event: Event) {
    this.saveDialog($event);
  }

  ngOnInit(): void {
    this.store$
      .pipe(
        select(selectNavigatedCustomerSpace),
        filter((x) => x.data !== null),
        map((x) => x.data),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((x) => {
        this.customerSpaceName = x.name;
        this.hasAbacusInstanceAndMandator =
          x.abacusMandator && Boolean(x.abacusMandator.abacusInstanceId) && Boolean(x.abacusMandator.abacusMandatorId);

        if (this.hasAbacusInstanceAndMandator) {
          this.abacusMandatorId = x.abacusMandator.abacusMandatorId;

          this.appPermissionsForm.get('payroll')?.enable();
          this.appPermissionsForm.get('creditor')?.enable();
        } else {
          this.updateEmployeeForm.get('isMailSent')!.disable();
          this.updateEmployeeForm.get('isMailSent')!.setValue(false);
          this.appPermissionsForm.get('linksAbacus')!.disable();
          this.appPermissionsForm.get('linksAbacus')!.setValue(false);
          this.appPermissionsForm.get('linksAbacusErp')!.disable();
          this.appPermissionsForm.get('linksAbacusErp')!.setValue(false);

          this.appPermissionsForm.get('payroll')?.disable();
          this.appPermissionsForm.get('creditor')?.disable();
        }
      });
  }

  public saveDialog($event: Event) {
    $event.preventDefault();
    $event.stopPropagation();

    if (!this.employee().value) {
      this.closeDialog();
      return;
    } else if (!this.updateEmployeeForm.valid) {
      return;
    }

    this.store$.dispatch(
      PlatformActions.closeDialog({
        id: this.dialogRef.id,
        data: {
          selectedEmployee: this.employee().value,
          selectedRoleId: this.roleId().value,
          selectedIsContact: this.isContact().value,
          selectedIsMailSent: this.isMailSent().value,
          selectedRoleDescription: this.roleDescription().value,
          appPermissions: this.getSelectedAppPermissions(),
          abacusMandatorId: this.abacusMandatorId,
        },
      }),
    );
  }

  public closeDialog(): void {
    this.store$.dispatch(PlatformActions.closeDialog({ id: this.dialogRef.id }));
  }

  public getTranslatedRoleName(name: RoleName): string {
    if (!name) {
      return null!;
    }

    return this.translate.instant(`roles.${name.toLocaleLowerCase()}`);
  }

  public getUserDisplayName(user: User): string {
    return getUserDisplayName(user);
  }

  public setSelectedEmployee(user: CustomerSpaceUser) {
    if (!user) {
      this.roleId().reset();
      this.roleId().disable();
      return;
    }

    this.roles = this.customerSpaceRoles.filter((r) => !r.isRestrictedToInternalUsers || user.isInternal);
    this.roleId().reset();
    this.roleId().enable();

    this.isInternalUser = user.isInternal;

    if (user.isInternal) {
      this.appPermissionsForm.get('linksAbacus')!.setValue(true);
      this.appPermissionsForm.get('linksAbacusErp')!.setValue(true);
    } else {
      this.appPermissionsForm.get('linksAbacus')!.setValue(false);
      this.appPermissionsForm.get('linksAbacusErp')!.setValue(false);
    }
  }

  public doesUserHaveAppPermission(user: CustomerSpaceUser, app: CustomerSpaceApp) {
    const appPermission = getPermissionForApp(app, this.customerSpaceId);

    return user.appPermissions.indexOf(appPermission) >= 0;
  }

  public doesUserHaveAppPermissionLocked(user: CustomerSpaceUser, app: CustomerSpaceApp) {
    const appPermission = getPermissionForApp(app, this.customerSpaceId);

    return user.lockedAppPermissions && user.lockedAppPermissions.indexOf(appPermission) >= 0;
  }

  public goToUserProfile(): void {
    if (!this.userId) {
      return;
    }

    this.closeDialog();
    this.store$.dispatch(go({ path: ['/', 'users', this.userId] }));
  }

  private setupEmployeeForm(selectedEmployee: CustomerSpaceUser) {
    this.updateEmployeeForm = this.formBuilder.group({
      employee: [selectedEmployee, [Validators.required, requireSelection]],
      roleId: [selectedEmployee?.customerSpaceRole?.id, [Validators.required]],
      isContact: [selectedEmployee?.isContact, null],
      isMailSent: [selectedEmployee?.isMailSent, null],
      roleDescription: [selectedEmployee?.roleDescription, null],
    });

    if (!!selectedEmployee) {
      this.userId = selectedEmployee.id;
      this.isInternalUser = selectedEmployee.isInternal;
      this.employee().disable();
      this.roles = this.customerSpaceRoles.filter((r) => !r.isRestrictedToInternalUsers || selectedEmployee.isInternal);
    } else {
      this.roleId().disable();
      this.userId = null;
    }

    this.filteredUsers$ = combineLatest([
      toObservable(this.filterUsersByCustomerSpace),
      this.employee().valueChanges.pipe(startWith('')),
    ]).pipe(map(([filterByCustomerSpace, val]) => this.filterUsers(filterByCustomerSpace, val)));

    this.roleId().valueChanges.subscribe((id) => {
      if (!id) {
        return;
      }

      const role = this.roles.find((r) => r.id === id);

      if (role?.name === 'CustomerSpaceAdmin') {
        this.setAppPermissionFormForAdmin();
      } else {
        this.resetAppPermissionFormToDefaults();
        this.lockAppPermissionsForUser(this.employee().value);
      }

      if (!this.hasAbacusInstanceAndMandator) {
        this.appPermissionsForm.get('payroll')!.setValue(false);
        this.appPermissionsForm.get('creditor')!.setValue(false);
        this.appPermissionsForm.get('payroll')!.disable();
        this.appPermissionsForm.get('creditor')!.disable();
      }
    });
  }

  private setupAppPermissionForm() {
    this.appPermissionsForm = this.formBuilder.group({
      documents: [false],
      tasks: [false],
      payroll: [false],
      creditor: [false],
      projects: [false],
      financialStatementWorkings: [false],
      audit: [false],
      taxDeclaration: [false],
      links: [false],
      linksAbacus: [false],
      linksAbacusErp: [false],
      linksAbacusObtErp: [false],
      linksTicketPortal: [false],
      linksObtWebsite: [false],
    });

    if (!!this.employee().value) {
      const selectedEmployee = this.employee().value;

      if (selectedEmployee.customerSpaceRole?.name === 'CustomerSpaceAdmin') {
        this.setAppPermissionFormForAdmin();
      } else {
        this.appPermissionsForm.get('documents')!.setValue(this.doesUserHaveAppPermission(selectedEmployee, 'DOCUMENTS'));
        this.appPermissionsForm.get('tasks')!.setValue(this.doesUserHaveAppPermission(selectedEmployee, 'TASKS'));
        this.appPermissionsForm.get('payroll')!.setValue(this.doesUserHaveAppPermission(selectedEmployee, 'PAYROLL'));
        this.appPermissionsForm.get('creditor')!.setValue(this.doesUserHaveAppPermission(selectedEmployee, 'CREDITOR'));
        this.appPermissionsForm.get('projects')!.setValue(this.doesUserHaveAppPermission(selectedEmployee, 'PROJECTS'));
        this.appPermissionsForm
          .get('financialStatementWorkings')!
          .setValue(this.doesUserHaveAppPermission(selectedEmployee, 'FILE_PROCESS_FINANCIAL_STATEMENT_WORKINGS'));
        this.appPermissionsForm.get('audit')!.setValue(this.doesUserHaveAppPermission(selectedEmployee, 'FILE_PROCESS_AUDIT'));
        this.appPermissionsForm
          .get('taxDeclaration')
          ?.setValue(this.doesUserHaveAppPermission(selectedEmployee, 'FILE_PROCESS_TAX_DECLARATION'));
        this.appPermissionsForm.get('links')!.setValue(this.doesUserHaveAppPermission(selectedEmployee, 'LINKS'));
        this.appPermissionsForm.get('linksAbacus')!.setValue(this.doesUserHaveAppPermission(selectedEmployee, 'LINKS_ABACUS'));
        this.appPermissionsForm.get('linksAbacusErp')!.setValue(this.doesUserHaveAppPermission(selectedEmployee, 'LINKS_ABACUS_ERP'));
        this.appPermissionsForm
          .get('linksAbacusObtErp')!
          .setValue(this.doesUserHaveAppPermission(selectedEmployee, 'LINKS_ABACUS_OBT_ERP'));
        this.appPermissionsForm.get('linksTicketPortal')!.setValue(this.doesUserHaveAppPermission(selectedEmployee, 'LINKS_TICKET_PORTAL'));
        this.appPermissionsForm.get('linksObtWebsite')!.setValue(this.doesUserHaveAppPermission(selectedEmployee, 'LINKS_OBT_WEBSITE'));

        this.lockAppPermissionsForUser(selectedEmployee);
      }
    } else {
      this.resetAppPermissionFormToDefaults();
    }

    this.appPermissionsForm.get('links')!.valueChanges.subscribe((v) => {
      if (!v) {
        this.appPermissionsForm.get('linksAbacus')!.disable();
        this.appPermissionsForm.get('linksAbacusErp')!.disable();
        this.appPermissionsForm.get('linksAbacusObtErp')!.disable();
        this.appPermissionsForm.get('linksTicketPortal')!.disable();
        this.appPermissionsForm.get('linksObtWebsite')!.disable();
      } else {
        this.appPermissionsForm.get('linksAbacus')!.enable();
        this.appPermissionsForm.get('linksAbacusErp')!.enable();
        this.appPermissionsForm.get('linksAbacusObtErp')!.enable();
        this.appPermissionsForm.get('linksTicketPortal')!.enable();
        this.appPermissionsForm.get('linksObtWebsite')!.enable();
      }
    });
  }

  private lockAppPermissionsForUser(selectedEmployee: CustomerSpaceUser) {
    if (!selectedEmployee) {
      this.appPermissionsForm.get('projects')!.enable();
      return;
    }

    if (this.doesUserHaveAppPermissionLocked(selectedEmployee, 'PROJECTS')) {
      this.appPermissionsForm.get('projects')!.setValue(true);
      this.appPermissionsForm.get('projects')!.disable();
    } else {
      this.appPermissionsForm.get('projects')!.enable();
    }
  }

  private resetAppPermissionFormToDefaults() {
    this.appPermissionsForm.get('documents')!.setValue(true);
    this.appPermissionsForm.get('tasks')!.setValue(true);
    this.appPermissionsForm.get('payroll')!.setValue(false);
    this.appPermissionsForm.get('creditor')!.setValue(false);
    this.appPermissionsForm.get('projects')!.setValue(false);
    this.appPermissionsForm.get('financialStatementWorkings')!.setValue(false);
    this.appPermissionsForm.get('taxDeclaration')!.setValue(false);
    this.appPermissionsForm.get('audit')!.setValue(false);
    this.appPermissionsForm.get('links')!.setValue(true);
    // this.appPermissionsForm.get('linksAbacus')!.setValue(true);
    // this.appPermissionsForm.get('linksAbacusErp')!.setValue(true);
    this.appPermissionsForm.get('linksAbacusObtErp')!.setValue(true);
    this.appPermissionsForm.get('linksTicketPortal')!.setValue(true);
    this.appPermissionsForm.get('linksObtWebsite')!.setValue(true);

    this.appPermissionsForm.enable();
  }

  private setAppPermissionFormForAdmin() {
    this.appPermissionsForm.get('documents')!.setValue(true);
    this.appPermissionsForm.get('tasks')!.setValue(true);
    this.appPermissionsForm.get('payroll')!.setValue(true);
    this.appPermissionsForm.get('creditor')!.setValue(true);
    this.appPermissionsForm.get('projects')!.setValue(true);
    this.appPermissionsForm.get('financialStatementWorkings')!.setValue(true);
    this.appPermissionsForm.get('taxDeclaration')!.setValue(true);
    this.appPermissionsForm.get('audit')!.setValue(true);
    this.appPermissionsForm.get('links')!.setValue(true);
    this.appPermissionsForm.get('linksAbacus')!.setValue(true);
    this.appPermissionsForm.get('linksAbacusErp')!.setValue(true);
    this.appPermissionsForm.get('linksAbacusObtErp')!.setValue(true);
    this.appPermissionsForm.get('linksTicketPortal')!.setValue(true);
    this.appPermissionsForm.get('linksObtWebsite')!.setValue(true);

    this.appPermissionsForm.disable();
  }

  private getSelectedAppPermissions(): string[] {
    const permissions: any[] = [];

    if (!this.roleId().value) {
      return permissions;
    }

    const role = this.roles.find((r) => r.id === this.roleId().value);

    if (role?.name === 'CustomerSpaceAdmin') {
      return permissions;
    }

    if (this.appPermissionsForm.get('documents')!.value) {
      permissions.push(getPermissionForApp('DOCUMENTS', this.customerSpaceId));
    }

    if (this.appPermissionsForm.get('tasks')!.value) {
      permissions.push(getPermissionForApp('TASKS', this.customerSpaceId));
    }

    if (this.appPermissionsForm.get('payroll')!.value) {
      permissions.push(getPermissionForApp('PAYROLL', this.customerSpaceId));
    }

    if (this.appPermissionsForm.get('creditor')!.value) {
      permissions.push(getPermissionForApp('CREDITOR', this.customerSpaceId));
    }

    if (this.appPermissionsForm.get('projects')!.value) {
      permissions.push(getPermissionForApp('PROJECTS', this.customerSpaceId));
    }

    if (this.appPermissionsForm.get('financialStatementWorkings')!.value) {
      permissions.push(getPermissionForApp('FILE_PROCESS_FINANCIAL_STATEMENT_WORKINGS', this.customerSpaceId));
    }

    if (this.appPermissionsForm.get('taxDeclaration')!.value) {
      permissions.push(getPermissionForApp('FILE_PROCESS_TAX_DECLARATION', this.customerSpaceId));
    }

    if (this.appPermissionsForm.get('audit')!.value) {
      permissions.push(getPermissionForApp('FILE_PROCESS_AUDIT', this.customerSpaceId));
    }

    if (this.appPermissionsForm.get('links')!.value) {
      permissions.push(getPermissionForApp('LINKS', this.customerSpaceId));
    }

    if (this.appPermissionsForm.get('links')!.value && this.appPermissionsForm.get('linksAbacus')!.value) {
      permissions.push(getPermissionForApp('LINKS_ABACUS', this.customerSpaceId));
    }

    if (this.appPermissionsForm.get('links')!.value && this.appPermissionsForm.get('linksAbacusErp')!.value) {
      permissions.push(getPermissionForApp('LINKS_ABACUS_ERP', this.customerSpaceId));
    }

    if (this.appPermissionsForm.get('links')!.value && this.appPermissionsForm.get('linksAbacusObtErp')!.value) {
      permissions.push(getPermissionForApp('LINKS_ABACUS_OBT_ERP', this.customerSpaceId));
    }

    if (this.appPermissionsForm.get('links')!.value && this.appPermissionsForm.get('linksTicketPortal')!.value) {
      permissions.push(getPermissionForApp('LINKS_TICKET_PORTAL', this.customerSpaceId));
    }

    if (this.appPermissionsForm.get('links')!.value && this.appPermissionsForm.get('linksObtWebsite')!.value) {
      permissions.push(getPermissionForApp('LINKS_OBT_WEBSITE', this.customerSpaceId));
    }

    return permissions;
  }

  private employee(): AbstractControl {
    return this.updateEmployeeForm.get('employee')!;
  }

  private roleId(): AbstractControl {
    return this.updateEmployeeForm.get('roleId')!;
  }

  private isContact(): AbstractControl {
    return this.updateEmployeeForm.get('isContact')!;
  }

  private isMailSent(): AbstractControl {
    return this.updateEmployeeForm.get('isMailSent')!;
  }

  private roleDescription(): AbstractControl {
    return this.updateEmployeeForm.get('roleDescription')!;
  }

  private filterUsers(filterByCustomerSpace: boolean, val: string): User[] {
    let users = this.activeUsers;
    if (filterByCustomerSpace && this.customerSpaceName) {
      const customerSpaceName = this.customerSpaceName.toLowerCase();
      users = users.filter((u) => u.isInternal || u.organizationUnit?.toLowerCase() === customerSpaceName);
    }

    if (val && val.length > 0) {
      users = users.filter((u) => this.getUserDisplayName(u).toLowerCase().includes(val.toLowerCase()));
    }

    return users;
  }
}
